import {
    Takeout2Post,
    Takeout2Processing,
    DeviceConfig as _DeviceConfig,
    Gateway as _Gateway,
    Group as _Group,
    Ota as _Ota,
    Registry as _Registry,
    PortStatus as _PortStatus,
    DeviceStatus as _DeviceStatus,
} from "generated/models";
import { ActuatorCommand } from "types/Actuator";
import { MemberDto } from "./Organisation";
import { RecipientMode } from "./common";
import { GatewayType } from "constant/gateway";

export type MessageType = {
    subject: string;
    text: string;
};

export enum PortStatus {
    On,
    FaultyHardware,
    FaultyIndividual,
    FaultyAll,
    NoDevice,
}

export type LDSBus = {
    id: number;
    power: boolean;
    status: PortStatus;
};

export type Device = {
    deviceId: string;
    app: any | null;
    appId?: string | null;
    ldsuUID: string;
    gatewayName: string;
    said: number;
    /**
     * Name of the LDSU
     */
    name: string;
    deviceName?: string;
    deviceType?: string;
    sensorName: string;
    sensorType: string;
    /** @deprecated Use the "name" instead */
    bus: string;
    cls: string;
    gatewayId: string;
    unit: string;
    units?: string[];
    min: number | null;
    max: number | null;
    accuracy: number | null;
    mode: any | null;
    obj: string;
    intvl: number;
    format: string;
    busName: string;
    isActuator: boolean;
    alertAvailable: boolean;
};

export type LDSU = {
    ldsu_uuid: string;
    gatewayName?: string;
    name: string;
    gatewayId?: string;
    gatewayType: GatewayType | null;
    bus?: string;
};

export type GatewayDeviceList = {
    allRegistry: any[];
    deviceList: Device[];
};

export type SensorTypeObject = {
    id: string;
    name: string;
};

/**
 * Backend response
 */
export type GatewayObj = "PCS_GW" | "NON-POE" | "LORAWAN-GW";

export type GatewayLDSUDetailsProps = {
    nameEditable: string;
    handleNameChange: (e: any) => void;
    isNameEditing: boolean;
    handleClickEdit: () => void;
    handleClickUpdate: () => Promise<void>;
    isRefreshing: boolean;
    refreshThisGateway: () => Promise<void>;
    timestampDetails: {
        heartbeat: number;
        info: boolean;
    };
    registryDetails: any;
    hasOnboard: React.SetStateAction<boolean>;
    gatewayId: string;
};

export type GatewayReading = {
    heartbeat: number;
    devices: GatewayReadingDevice[];
};

export type GatewayReadingDevice = {
    UID: string;
    ts: number;
    value: number[];
    min: number[];
    max: number[];
};

export type RecipientConfig = {
    recipients: MemberDto[];
    isEnabled: boolean;
    recipientMode: RecipientMode;
};

export type AlertConfig = Record<RecipientMode, RecipientConfig>;

export type DownloadOptions = "email" | "fast";

export type DownloadIntervalTime = Takeout2Processing;

export type DownloadPayload = Takeout2Post;

export type DeviceConfig = Omit<_DeviceConfig, "CMDS"> & {
    CMDS: ActuatorCommand[];
};

export type Group = Omit<_Group, "gateways"> & {
    gateways: Registry[];
};

export type Port = Omit<_PortStatus, "PORT"> & {
    PORT: PortStatus[];
};

export type Gateway = Omit<_Gateway, "bus_power"> & {
    bus_power: boolean[];
    registry?: _Registry;
    devices: any[];
    model: string;
    WASC: string;
    WSCS: string;
};

export type Ota = Omit<_Ota, "create_time" | "modify_time"> & {
    /* Override the Date type from the generated file */
    create_time: string;
    /* Override the Date type from the generated file */
    modify_time: string;
};

export enum DeviceStatusStatus {
    NOT_READY = "NOT_READY",
    ONLINE = "ONLINE",
    OFFLINE = "OFFLINE",
}

export type DeviceStatus = Omit<_DeviceStatus, "status"> & {
    status: DeviceStatusStatus;
};

export type Registry = Omit<
    _Registry,
    "info" | "ota" | "configs" | "device_statuses"
> & {
    info?: Gateway;
    ota?: Ota;
    configs: DeviceConfig[];
    port?: Port;
    device_statuses: DeviceStatus[];
};

/**
 * Log entry types for Gateway Log
 */
export enum GatewayLogType {
    Info = "Info",
    Error = "Error",
}

/**
 * Journal entry attributes from API
 */
export type JournalAttributes = {
    child_uuid?: string;
    child_uuid2?: string;
    said?: string;
    level?: string;
    status?: string;
    gateway_message?: string | Record<string, unknown>;
};

/**
 * Raw journal entry from /gateways/journals API
 */
export type JournalEntry = {
    uuid: string;
    create_time: string;
    modify_time: string;
    user: string;
    operation: string;
    resource: string;
    resource_uuid: string;
    attributes: JournalAttributes;
};

/**
 * Gateway Log entry type (transformed from JournalEntry)
 */
export type GatewayLogEntry = {
    uuid: string;
    event_name: string;
    description: string;
    timestamp: string;
    type: GatewayLogType;
};
